import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FiActivity, FiCheck, FiX, FiPause, FiClock, FiLayers, FiFileText, FiUser } from 'react-icons/fi';
import { useTintedBackground } from '../hooks/useTintedBackground';
import { Spinner } from '../components/common/Spinner';
import { useTimelinePreferences } from '../hooks/useTimelinePreferences';

// Types — exported so TimelineCanvas can import them
export interface TimelineExecution {
  id: string;
  workflow_id: string;
  workflow_name: string;
  workflow_description?: string;
  status: 'active' | 'completed' | 'failed' | 'paused' | 'unknown';
  created_at: string;
  updated_at: string;
  end_time?: string;
  duration_seconds?: number;
  progress_percent: number;
  current_phase?: any;
  phases_summary: Array<{
    id: string;
    name: string;
    status: string;
    duration?: number;
  }>;
  artifacts_count: number;
  phases_count: number;
  phases_completed: number;
  session_id: string;
  tags?: string[];
  personas?: string[];
}

export interface TimelineStats {
  counts: {
    active: number;
    completed: number;
    failed: number;
    paused: number;
    total: number;
  };
  latest_execution_time?: string;
}

// API functions using the configured api service
import api from '../services/api';

const fetchTimeline = async (params: Record<string, string>): Promise<{ executions: TimelineExecution[]; pagination: any }> => {
  const queryParams = new URLSearchParams(params);
  try {
    const response = await api.get(`/timeline?${queryParams}`);
    return response.data;
  } catch (error: any) {
    console.error('Timeline fetch error:', error);
    if (error.response?.status === 404) {
      throw new Error('Timeline endpoint not found. Please ensure the backend is running and has been restarted to include the timeline routes.');
    }
    throw new Error(error.response?.data?.detail || 'Failed to fetch timeline');
  }
};

const fetchTimelineStats = async (params: Record<string, string>): Promise<TimelineStats> => {
  const { limit, offset, ...rest } = params;
  const queryParams = new URLSearchParams(rest);
  try {
    const response = await api.get(`/timeline/stats?${queryParams}`);
    return response.data;
  } catch (error: any) {
    console.error('Stats fetch error:', error);
    if (error.response?.status === 404) {
      throw new Error('Timeline stats endpoint not found');
    }
    throw new Error(error.response?.data?.detail || 'Failed to fetch stats');
  }
};

const fetchExecutionDetails = async (executionId: string): Promise<any> => {
  try {
    const response = await api.get(`/timeline/execution/${executionId}/details`);
    return response.data;
  } catch (error: any) {
    console.error('Execution details fetch error:', error);
    throw new Error(error.response?.data?.detail || 'Failed to fetch execution details');
  }
};

// Status icon component
const StatusIcon: React.FC<{ status: string; size?: 'sm' | 'md' | 'lg' }> = ({ status, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
  };

  switch (status) {
    case 'active':
      return <div className={`${sizeClasses[size]} rounded-full bg-blue-500 animate-pulse`} />;
    case 'completed':
      return <FiCheck className={`${sizeClasses[size]} text-green-500`} />;
    case 'failed':
      return <FiX className={`${sizeClasses[size]} text-red-500`} />;
    case 'paused':
      return <FiPause className={`${sizeClasses[size]} text-yellow-500`} />;
    default:
      return <div className={`${sizeClasses[size]} rounded-full bg-gray-500`} />;
  }
};

// Format time ago
const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Format duration
const formatDuration = (seconds?: number): string => {
  if (!seconds) return '--';
  const mins = Math.floor(seconds / 60);
  const hours = Math.floor(mins / 60);
  if (hours > 0) return `${hours}h ${mins % 60}m`;
  return `${mins}m`;
};

// Execution Card Component
const ExecutionCard: React.FC<{
  execution: TimelineExecution;
  isExpanded: boolean;
  onToggle: () => void;
  darkMode: boolean;
}> = ({ execution, isExpanded, onToggle, darkMode }) => {
  const [details, setDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  // Ref guard to prevent the auto-expand useEffect from firing more than once
  const autoExpandedRef = useRef(false);

  const loadDetails = async () => {
    if (details || loadingDetails) return;
    setLoadingDetails(true);
    try {
      const data = await fetchExecutionDetails(execution.id);
      setDetails(data);
    } catch (e) {
      console.error('Failed to load details:', e);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleToggle = () => {
    onToggle();
    if (!isExpanded) {
      loadDetails();
    }
  };

  // Auto-expand failed executions — guarded with ref to prevent infinite loop
  useEffect(() => {
    if (
      execution.status === 'failed' &&
      !isExpanded &&
      !autoExpandedRef.current
    ) {
      autoExpandedRef.current = true;
      handleToggle();
    }
  }, [execution.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const isActive = execution.status === 'active';

  return (
    <div
      className={`
        relative rounded-xl border-2 transition-all duration-300 overflow-hidden
        ${isActive
          ? darkMode
            ? 'border-blue-500/50 shadow-lg shadow-blue-500/20'
            : 'border-blue-400 shadow-lg shadow-blue-400/20'
          : darkMode
            ? 'border-gray-700 hover:border-gray-600'
            : 'border-gray-200 hover:border-gray-300'
        }
        ${darkMode ? 'bg-gray-800' : 'bg-white'}
      `}
    >
      {/* Compact View */}
      <div
        className="p-4 cursor-pointer"
        onClick={handleToggle}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <StatusIcon status={execution.status} size="lg" />
            <div>
              <h3 className={`font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                {execution.workflow_name}
              </h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {formatTimeAgo(execution.created_at)}
              </p>
            </div>
          </div>

          <div className="text-right">
            <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {execution.progress_percent}%
            </div>
            <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {execution.phases_completed}/{execution.phases_count} phases
            </div>
          </div>
        </div>

        {/* Mini Progress Bar */}
        <div className="mt-3">
          <div className={`h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                execution.status === 'failed' ? 'bg-red-500' :
                execution.status === 'completed' ? 'bg-green-500' :
                'bg-blue-500'
              }`}
              style={{ width: `${execution.progress_percent}%` }}
            />
          </div>
        </div>

        {/* Tags & Personas row */}
        {((execution.tags && execution.tags.length > 0) || (execution.personas && execution.personas.length > 0)) && (
          <div className="mt-2 flex flex-wrap gap-1">
            {execution.personas?.slice(0, 2).map(persona => (
              <span key={persona} className={`
                text-xs px-1.5 py-0.5 rounded-full flex items-center gap-1
                ${darkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}
              `}>
                <FiUser className="w-2.5 h-2.5" />
                {persona}
              </span>
            ))}
            {execution.tags?.slice(0, 3).map(tag => (
              <span key={tag} className={`
                text-xs px-1.5 py-0.5 rounded-full
                ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'}
              `}>
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Expanded View */}
      {isExpanded && (
        <div className={`px-4 pb-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {loadingDetails ? (
            <div className="py-4 flex justify-center">
              <Spinner size="sm" />
            </div>
          ) : (
            <>
              {/* Phase Breakdown */}
              <div className="py-3">
                <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Phase Progress
                </h4>
                <div className="space-y-2">
                  {execution.phases_summary.map((phase, idx) => (
                    <div key={phase.id} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                          phase.status === 'completed'
                            ? 'bg-green-500 text-white'
                            : phase.status === 'in_progress'
                              ? 'bg-blue-500 text-white'
                              : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {idx + 1}
                        </span>
                        <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{phase.name}</span>
                      </div>
                      <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {phase.duration ? formatDuration(phase.duration) : '--'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Artifacts & Metadata */}
              {(execution.artifacts_count > 0 || details?.phases?.some((p: any) => p.artifacts?.length > 0)) && (
                <div className={`py-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Artifacts ({execution.artifacts_count})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {details?.phases?.flatMap((p: any) => p.artifacts || []).slice(0, 5).map((artifact: any) => (
                      <span
                        key={artifact.id}
                        className={`text-xs px-2 py-1 rounded ${
                          darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        <FiFileText className="inline w-3 h-3 mr-1" />
                        {artifact.name}
                      </span>
                    ))}
                    {execution.artifacts_count > 5 && (
                      <span className={`text-xs px-2 py-1 rounded ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        +{execution.artifacts_count - 5} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className={`pt-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex gap-2`}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/workflows/executions/${execution.id}`, '_blank');
                  }}
                  className={`text-xs px-3 py-1.5 rounded ${
                    darkMode
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  View Details
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/workflows/${execution.workflow_id}`, '_blank');
                  }}
                  className={`text-xs px-3 py-1.5 rounded ${
                    darkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  View Workflow
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Import new components
import { TimelineCanvas } from '../components/timeline/TimelineCanvas';
import { TimelineControls } from '../components/timeline/TimelineControls';

// Main Timeline Page
const TimelinePage: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // localStorage preferences
  const { prefs, updatePref } = useTimelinePreferences();

  // URL State — fall back to localStorage prefs when URL param absent
  const range = searchParams.get('range') || prefs.defaultRange;
  const status = searchParams.get('status') || 'all';
  const search = searchParams.get('search') || '';
  const workflowId = searchParams.get('workflow_id') || '';
  const viewModeParam = searchParams.get('view') || 'list';

  // Local State
  const [expandedExecution, setExpandedExecution] = useState<string | null>(null);
  const [liveUpdates, setLiveUpdates] = useState(true);
  const [hasNewData, setHasNewData] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrolledBack, setIsScrolledBack] = useState(false);

  // Pagination state
  const [allExecutions, setAllExecutions] = useState<TimelineExecution[]>([]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // View mode state
  const [viewMode, setViewMode] = useState<'vertical' | 'horizontal'>(
    viewModeParam === 'timeline' ? 'horizontal' : 'vertical'
  );
  const [zoomLevel, setZoomLevel] = useState(1);
  const [groupBy, setGroupBy] = useState<'workflow' | 'status' | 'none'>('workflow');

  // Tinted background for header
  const tintedBg = useTintedBackground({
    tintColor: darkMode ? '#3b82f6' : '#6366f1',
    enabled: true
  });

  // Calculate time range for horizontal view
  const timeRange = useMemo(() => {
    const end = new Date();
    const start = new Date();

    switch (range) {
      case '24h':
        start.setHours(start.getHours() - 24);
        break;
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      default:
        start.setDate(start.getDate() - 7);
    }

    return { start, end };
  }, [range]);

  // Build query params — memoized to prevent request storms
  const queryParams = useMemo(() => {
    const params: Record<string, string> = {
      limit: '20',
      offset: String(currentOffset)
    };

    if (range !== 'all') {
      const now = new Date();
      let startDate = new Date();
      switch (range) {
        case '24h':
          startDate.setHours(now.getHours() - 24);
          break;
        case '7d':
          startDate.setDate(now.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(now.getDate() - 30);
          break;
      }
      params.start_date = startDate.toISOString();
    }

    if (status && status !== 'all') params.status = status;
    if (search) params.search = search;
    if (workflowId) params.workflow_id = workflowId;

    return params;
  }, [range, status, search, workflowId, currentOffset]);

  // Reset accumulated state when filters change (not offset)
  const filterKey = `${range}|${status}|${search}|${workflowId}`;
  const prevFilterKeyRef = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKeyRef.current !== filterKey) {
      prevFilterKeyRef.current = filterKey;
      setAllExecutions([]);
      setCurrentOffset(0);
    }
  }, [filterKey]);

  // Fetch timeline data with retry configuration
  const { data: timelineData, isLoading, error } = useQuery(
    ['timeline', queryParams],
    () => fetchTimeline(queryParams),
    {
      refetchInterval: liveUpdates && !isScrolledBack ? 30000 : false,
      staleTime: 30000,
      retry: (failureCount, error: any) => {
        if (error?.status === 429 || error?.status === 404) return false;
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      onSuccess: (data) => {
        setAllExecutions(prev =>
          currentOffset === 0
            ? data.executions
            : [...prev, ...data.executions]
        );
        setHasMore(data.pagination.has_more);
        setTotalCount(data.pagination.total);
        setLastUpdate(new Date());
        setHasNewData(false);
      },
      onError: (err: any) => {
        if (err?.status === 429) {
          console.warn('Rate limited by timeline API, pausing live updates temporarily');
          setLiveUpdates(false);
          setTimeout(() => setLiveUpdates(true), 60000);
        }
      }
    }
  );

  // Fetch stats
  const { data: stats } = useQuery(
    ['timelineStats', queryParams],
    () => fetchTimelineStats(queryParams),
    {
      refetchInterval: liveUpdates && !isScrolledBack ? 30000 : false,
      staleTime: 30000,
      retry: (failureCount, error: any) => {
        if (error?.status === 429 || error?.status === 404) return false;
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000)
    }
  );

  // Check for new data
  useEffect(() => {
    if (stats?.latest_execution_time) {
      const latest = new Date(stats.latest_execution_time);
      if (latest > lastUpdate) {
        setHasNewData(true);
      }
    }
  }, [stats, lastUpdate]);

  // Handle scroll for pausing updates
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;
    const { scrollTop } = scrollContainerRef.current;
    setIsScrolledBack(scrollTop > 500);
  }, []);

  // Update URL params
  const updateParams = useCallback((updates: Record<string, string>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Toggle execution expansion
  const toggleExecution = useCallback((id: string) => {
    setExpandedExecution(prev => prev === id ? null : id);
  }, []);

  // Load More handler
  const handleLoadMore = useCallback(() => {
    setCurrentOffset(prev => prev + 20);
  }, []);

  // Refresh data
  const handleRefresh = () => {
    setAllExecutions([]);
    setCurrentOffset(0);
    queryClient.invalidateQueries(['timeline']);
    queryClient.invalidateQueries(['timelineStats']);
    setHasNewData(false);
  };

  // Handle view mode change
  const handleViewModeChange = (mode: 'vertical' | 'horizontal') => {
    setViewMode(mode);
    const newParams = new URLSearchParams(searchParams);
    if (mode === 'horizontal') newParams.set('view', 'timeline');
    else newParams.set('view', 'list');
    setSearchParams(newParams);
  };

  // Keyboard navigation
  const executionIds = useMemo(
    () => allExecutions.map(e => e.id),
    [allExecutions]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't fire when user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) return;

      const currentIndex = expandedExecution
        ? executionIds.indexOf(expandedExecution)
        : -1;

      switch (e.key) {
        case 'j':
        case 'J': {
          e.preventDefault();
          const next = currentIndex < executionIds.length - 1
            ? executionIds[currentIndex + 1]
            : executionIds[0];
          if (next) setExpandedExecution(next);
          break;
        }
        case 'k':
        case 'K': {
          e.preventDefault();
          const prev = currentIndex > 0
            ? executionIds[currentIndex - 1]
            : executionIds[executionIds.length - 1];
          if (prev) setExpandedExecution(prev);
          break;
        }
        case 'Enter': {
          if (expandedExecution) toggleExecution(expandedExecution);
          break;
        }
        case 'Escape': {
          setExpandedExecution(null);
          break;
        }
        case '/': {
          e.preventDefault();
          document.querySelector<HTMLInputElement>('input[placeholder="Search workflows..."]')?.focus();
          break;
        }
        case ' ': {
          e.preventDefault();
          setLiveUpdates(prev => !prev);
          break;
        }
        case '1': updateParams({ range: '24h' }); break;
        case '2': updateParams({ range: '7d' }); break;
        case '3': updateParams({ range: '30d' }); break;
        case '4': updateParams({ range: '' }); break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [expandedExecution, executionIds, toggleExecution, updateParams]);

  if (error) {
    return (
      <div className={`p-8 text-center ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
        <FiX className="w-12 h-12 mx-auto mb-4" />
        <h2 className="text-xl font-bold mb-2">Failed to load timeline</h2>
        <p>{error instanceof Error ? error.message : 'Unknown error'}</p>
        <button
          onClick={handleRefresh}
          className={`mt-4 px-4 py-2 rounded ${darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'}`}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`h-screen flex flex-col ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header with tinted glass */}
      <div
        ref={tintedBg.ref}
        className={`
          relative border-b shadow-lg
          ${darkMode ? 'border-gray-700/50' : 'border-gray-200/50'}
        `}
        style={tintedBg.style}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={tintedBg.overlayStyle}
        />

        {/* Banner */}
        <div className={`
          relative z-10 w-full py-1.5 px-4 border-b
          ${darkMode
            ? 'bg-gradient-to-r from-blue-900/80 via-indigo-900/80 to-blue-900/80 border-gray-700/50'
            : 'bg-gradient-to-r from-blue-600/90 via-indigo-600/90 to-blue-600/90 border-gray-200/50'
          }
        `}>
          <div className="flex items-center justify-center">
            <div className={`
              text-xs font-black tracking-[0.3em] uppercase
              ${darkMode ? 'text-blue-200' : 'text-white'}
            `}>
              WORKFLOW TIMELINE
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className={`
          relative z-10 px-4 py-3 flex flex-wrap items-center gap-4
          ${darkMode
            ? 'bg-gradient-to-r from-gray-900/90 via-gray-800/90 to-gray-900/90'
            : 'bg-gradient-to-r from-white/90 via-gray-50/90 to-white/90'
          }
          backdrop-blur-md
        `}>
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search workflows..."
              value={search}
              onChange={(e) => updateParams({ search: e.target.value })}
              className={`
                w-full px-4 py-2 rounded-lg text-sm
                border focus:outline-none focus:ring-2
                ${darkMode
                  ? 'bg-gray-800/80 border-gray-600 text-gray-100 placeholder-gray-500 focus:ring-blue-500/50'
                  : 'bg-white/80 border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500/30'
                }
              `}
            />
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            {[
              { key: '24h', label: '24h' },
              { key: '7d', label: '7d' },
              { key: '30d', label: '30d' },
              { key: 'all', label: 'All' }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => {
                  const newRange = key === 'all' ? '' : key;
                  updateParams({ range: newRange });
                  if (newRange && newRange !== 'all') {
                    updatePref('defaultRange', newRange as '24h' | '7d' | '30d');
                  }
                }}
                className={`
                  px-3 py-1.5 rounded-md text-sm font-medium transition-all
                  ${range === key || (key === 'all' && !range)
                    ? darkMode
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-500 text-white'
                    : darkMode
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }
                `}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Status Filter */}
          <select
            value={status}
            onChange={(e) => updateParams({ status: e.target.value })}
            className={`
              px-3 py-2 rounded-lg text-sm
              border focus:outline-none
              ${darkMode
                ? 'bg-gray-800 border-gray-600 text-gray-100'
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="paused">Paused</option>
          </select>

          {/* Live Toggle */}
          <button
            onClick={() => setLiveUpdates(!liveUpdates)}
            className={`
              flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all
              ${liveUpdates
                ? darkMode
                  ? 'bg-green-900/50 text-green-400 border border-green-700'
                  : 'bg-green-100 text-green-700 border border-green-300'
                : darkMode
                  ? 'bg-gray-800 text-gray-400 border border-gray-700'
                  : 'bg-white text-gray-600 border border-gray-300'
              }
            `}
          >
            <div className={`w-2 h-2 rounded-full ${liveUpdates ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
            Live
          </button>
        </div>
      </div>

      {/* New Data Notification */}
      {hasNewData && (
        <div
          className={`
            px-4 py-2 text-center text-sm cursor-pointer
            ${darkMode ? 'bg-blue-900/50 text-blue-300 hover:bg-blue-900/70' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}
          `}
          onClick={handleRefresh}
        >
          New executions available — Click to refresh
        </div>
      )}

      {/* View Controls */}
      <TimelineControls
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        zoomLevel={zoomLevel}
        onZoomChange={setZoomLevel}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
      />

      {/* Main Content */}
      {viewMode === 'horizontal' ? (
        <div className="flex-1 overflow-hidden">
          {isLoading && allExecutions.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <Spinner size="lg" />
            </div>
          ) : allExecutions.length === 0 ? (
            <div className={`
              flex flex-col items-center justify-center h-full
              ${darkMode ? 'text-gray-400' : 'text-gray-500'}
            `}>
              <FiActivity className="w-12 h-12 mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2">No executions found</h3>
              <p>Try adjusting your filters or create a new workflow execution</p>
            </div>
          ) : (
            <TimelineCanvas
              executions={allExecutions}
              selectedExecution={expandedExecution}
              onSelectExecution={toggleExecution}
              timeRange={timeRange}
              onTimeRangeChange={(range) => {
                const newParams = new URLSearchParams(searchParams);
                if (range.start) newParams.set('start_date', range.start.toISOString());
                if (range.end) newParams.set('end_date', range.end.toISOString());
                setSearchParams(newParams);
              }}
              groupBy={groupBy}
              autoScroll={true}
            />
          )}
        </div>
      ) : (
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-4"
        >
          {isLoading && allExecutions.length === 0 ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : allExecutions.length === 0 ? (
            <div className={`
              text-center py-12 rounded-xl border-2 border-dashed
              ${darkMode ? 'bg-gray-800/50 border-gray-700 text-gray-400' : 'bg-gray-100 border-gray-200 text-gray-500'}
            `}>
              <FiActivity className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2">No executions found</h3>
              <p>Try adjusting your filters or create a new workflow execution</p>
            </div>
          ) : (
            <>
              {/* Vertical timeline with connector spine */}
              <div className="relative">
                {/* Spine line */}
                <div className={`
                  absolute left-[27px] top-0 bottom-0 w-0.5
                  ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}
                `} />

                {allExecutions.map((execution) => (
                  <div key={execution.id} className="relative pl-14 mb-4">
                    {/* Status dot on the spine */}
                    <div className={`
                      absolute left-5 top-6 w-5 h-5 rounded-full
                      flex items-center justify-center z-10
                      ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}
                      ring-2 ${
                        execution.status === 'active' ? 'ring-blue-500' :
                        execution.status === 'completed' ? 'ring-green-500' :
                        execution.status === 'failed' ? 'ring-red-500' :
                        'ring-yellow-500'
                      }
                    `}>
                      <StatusIcon status={execution.status} size="sm" />
                    </div>

                    <ExecutionCard
                      execution={execution}
                      isExpanded={expandedExecution === execution.id}
                      onToggle={() => toggleExecution(execution.id)}
                      darkMode={darkMode}
                    />
                  </div>
                ))}
              </div>

              {/* Load More */}
              {hasMore && (
                <div className="text-center py-4">
                  <button
                    onClick={handleLoadMore}
                    disabled={isLoading}
                    className={`
                      px-4 py-2 rounded-lg text-sm flex items-center gap-2 mx-auto
                      ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-100'}
                      disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                  >
                    {isLoading ? <Spinner size="sm" /> : `Load More (${totalCount - allExecutions.length} remaining)`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Status Footer */}
      <div className={`
        border-t px-4 py-3 flex items-center justify-between
        ${darkMode
          ? 'bg-gray-800 border-gray-700'
          : 'bg-white border-gray-200'
        }
      `}>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {stats?.counts.active || 0} Active
            </span>
          </div>
          <div className="flex items-center gap-2">
            <FiCheck className="w-4 h-4 text-green-500" />
            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {stats?.counts.completed || 0} Completed
            </span>
          </div>
          <div className="flex items-center gap-2">
            <FiX className="w-4 h-4 text-red-500" />
            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {stats?.counts.failed || 0} Failed
            </span>
          </div>
        </div>

        <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Last updated: {lastUpdate.toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};

export default TimelinePage;
